/**
 * PaymentAnalytics — summary stats over the payments carried by the artifact
 * (from SearchPayments). Shows the total match count and a breakdown by
 * paymentDetails.paymentStatus. Redux-connected to the searchPayments slice;
 * renders the raw summaries directly (no bespoke mapping).
 */
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Spinner from '@cloudscape-design/components/spinner';
import { useAppSelector } from 'src/store/hooks';

export const PaymentAnalytics = () => {
  const { payments, total, loading, error } = useAppSelector(state => state.searchPayments);

  // Breakdown by paymentDetails.paymentStatus (plugin/bank-level status).
  const counts: Record<string, number> = {};
  payments.forEach(p => {
    const status = p.paymentDetails?.paymentStatus;
    const key = status && status !== 'null' ? status : 'Unknown';
    counts[key] = (counts[key] ?? 0) + 1;
  });
  const buckets = Object.entries(counts).sort((a, b) => b[1] - a[1]);

  return (
    <Container header={<Header variant="h2">Payment analytics</Header>}>
      {loading ? (
        <Box textAlign="center" padding="m"><Spinner /></Box>
      ) : error ? (
        <Box color="text-status-error">{error}</Box>
      ) : (
        <ColumnLayout columns={Math.min(buckets.length + 1, 4)} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Total payments</Box>
            <Box variant="awsui-value-large">{total}</Box>
          </div>
          {buckets.map(([status, count]) => (
            <div key={status}>
              <Box variant="awsui-key-label">{status}</Box>
              <Box variant="awsui-value-large">{count}</Box>
            </div>
          ))}
        </ColumnLayout>
      )}
    </Container>
  );
};
